import React, { FC, useEffect, useRef, useState } from "react";
import { ActivityIndicator, ScrollView, StyleSheet, View } from "react-native";
import { useNavigation, useRoute } from "@react-navigation/native";
import { useSelector } from "react-redux";
import { useAppDispatch } from "src/redux";
import { kColors } from "src/kColors";
import { kStyleConst } from "src/kStyle";
import { validateEmail } from "src/redux/user/actions";
import { BrandApplication, RepApplication, RetailApplication, UserApplicationInfo } from "../components";
import { kRoute } from "../kRoute";
import { tValidType } from "src/components/FormInputs";
import { PdButton, PdButtonLabel } from "src/components/forms/button";
import { useIsMobile } from "src/hooks/ui";
import { pushApplicationData } from "src/redux/Application/actions";
import * as asyncStorage from "src/util/asyncStorage";

type tApplicationParams = {
  params?: { applicationType?: string; email?: string };
  key: string;
  name: string;
};

const P01_01_Application: FC = () => {
  const kNav = useNavigation();
  const route = useRoute<tApplicationParams>();
  const dispatch = useAppDispatch();
  const isMobile = useIsMobile();
  const rApplication = useSelector((state) => state.ApplicationReducer);
  const [isValid, setValid] = useState<tValidType>({});
  const [isLoading, setLoading] = useState(true);
  const applicationTypeRef = useRef(rApplication.type);

  const handleOnApply = async () => {
    if (Object.values(isValid).filter((aIsValid) => aIsValid === false).length !== 0) {
      dispatch({ type: "APPLICATION_SHOW_VALIDATION", isShown: true });
      dispatch({
        type: "RIBBON",
        message: new Error("Missing required fields above."),
      });
      return;
    } else {
      try {
        setLoading(true);
        await dispatch(pushApplicationData(rApplication));
      } catch (er) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (route.params?.applicationType === "Rep") {
      dispatch({ type: "UPDATE_TYPE", newType: "rep" });
      if (route.params?.email !== undefined && validateEmail(route.params.email)) {
        dispatch({ type: "UPDATE_REP_APPLICATION", applicationPart: { contact: { email: route.params.email } } });
        dispatch({ type: "DISABLE_EMAIL", emailDisabled: true });
      }
    } else if (route.params?.applicationType === "Retailer") {
      dispatch({ type: "UPDATE_TYPE", newType: "retailer" });
      if (route.params.email !== undefined && validateEmail(route.params.email)) {
        dispatch({ type: "UPDATE_RETAIL_APPLICATION", applicationPart: { contact: { email: route.params.email } } });
        dispatch({ type: "DISABLE_EMAIL", emailDisabled: true });
      }
    } else if (route.params?.applicationType === "Brand") {
      dispatch({ type: "UPDATE_TYPE", newType: "brand" });
      if (route.params.email !== undefined && validateEmail(route.params.email)) {
        dispatch({ type: "UPDATE_BRAND_APPLICATION", applicationPart: { contact: { email: route.params.email } } });
        dispatch({ type: "DISABLE_EMAIL", emailDisabled: true });
      }
    } else {
      dispatch({ type: "UPDATE_BRAND_APPLICATION", applicationPart: {}, reset: true });
    }

    setLoading(false);
  }, [dispatch, route.params?.applicationType, route.params?.email]);

  useEffect(() => {
    (async () => {
      const maybeReferralId = await asyncStorage.getData("referralId");
      if (maybeReferralId) {
        dispatch({ type: "UPDATE_BRAND_APPLICATION", applicationPart: { hearAboutUs: "referral" } });
        dispatch({ type: "UPDATE_RETAIL_APPLICATION", applicationPart: { hearAboutUs: "referral" } });
        dispatch({ type: "UPDATE_REP_APPLICATION", applicationPart: { hearAboutUs: "referral" } });
        dispatch({ type: "DISABLE_HEAR_ABOUT_US", payload: true });
      }
    })();
  });

  useEffect(() => {
    if (rApplication.applicationSent) {
      kNav.navigate(kRoute.P01_02);
    }
  }, [dispatch, kNav, rApplication.applicationSent]);

  useEffect(() => {
    if (applicationTypeRef.current !== rApplication.type) {
      applicationTypeRef.current = rApplication.type;
      setValid({});
    }
  }, [rApplication.type, setValid]);

  if (isLoading) {
    return (
      <View style={[styles.flex1, styles.alignCenter, styles.justifyCenter]}>
        <ActivityIndicator size="large" color={kColors.brandGreen} />
      </View>
    );
  }

  return (
    <ScrollView contentContainerStyle={styles.container} style={styles.bgBrandLight}>
      <UserApplicationInfo loggedIn={false} />
      {rApplication.type === "brand" && <BrandApplication isValid={isValid} setValid={setValid} />}
      {rApplication.type === "retailer" && <RetailApplication isValid={isValid} setValid={setValid} />}
      {rApplication.type === "rep" && <RepApplication isValid={isValid} setValid={setValid} />}
      <View style={styles.section}>
        <PdButton style={isMobile ? {} : styles.mlAuto} onPress={handleOnApply}>
          <PdButtonLabel>Apply</PdButtonLabel>
        </PdButton>
      </View>
    </ScrollView>
  );
};

export const P01_01 = React.memo(P01_01_Application);

const styles = StyleSheet.create({
  alignCenter: {
    alignItems: "center",
  },
  bgBrandLight: {
    backgroundColor: kColors.brandLight,
  },
  container: {
    borderRadius: 4 * kStyleConst.kBorderRadius,
    flexGrow: 1,
    marginHorizontal: 8 * kStyleConst.kSpacing,
  },
  flex1: {
    flex: 1,
  },
  justifyCenter: {
    justifyContent: "center",
  },
  mlAuto: {
    marginLeft: "auto",
  },
  section: {
    backgroundColor: kColors.white,
    borderRadius: 8,
    flexGrow: 1,
    marginVertical: 4 * kStyleConst.kSpacing,
    paddingHorizontal: 13 * kStyleConst.kSpacing,
    paddingVertical: 6 * kStyleConst.kSpacing,
  },
});
